<script setup lang="ts">
import { useI18n } from 'vue-i18n'
const { t, locale } = useI18n()

defineProps({
    expectedDeliveryDate: {
      type: String,
      required: true,
    },
    carrierHandoverDate: {
      type: String,
      required: true,
    },
    porductIndex: {
      type: Boolean,
      default: false,
    },
    immediateDelivery: {
      type: Boolean,
      default: false,
    }
});

type RelativeDayResult = "today" | "tomorrow" | "dayAfterTomorrow" | string;

function getRelativeDay(
  inputDate: Date | string | number,
): RelativeDayResult {
  const target = new Date(inputDate);
  if (isNaN(target.getTime())) return "";

  const today = new Date();

  today.setHours(0, 0, 0, 0);
  target.setHours(0, 0, 0, 0);

  const diffDays =
    (target.getTime() - today.getTime()) / (1000 * 60 * 60 * 24);

  if (diffDays === 0) return t('today');
  if (diffDays === 1) return t('tomorrow');
  if (diffDays === 2) return t('day_after_tomorrow');

  return target.toLocaleDateString();
}
</script>

<template>
  <div
    class="flex c-flex-wrap"
    :class="porductIndex ? 'text-xs c-items-center' : 'text-sm c-items-center'"
  >
    <i class="mi-delivery icon-20 mr-1" />
    <span v-if="immediateDelivery">
      {{ t('estimated_delivery_date') }}:
      <strong class="text-success">{{ t('now') }}</strong>
    </span>
    <span
      v-else-if="locale === 'cs' || locale === 'sk'"
      class="flex-1"
      :class="{ 'mt-1': !porductIndex }"
    >
      {{ t('estimated_delivery_date') }}:
      <strong class="text-success">{{ getRelativeDay(expectedDeliveryDate) }}</strong>
    </span>
    <span
      v-else
      class="flex-1"
      :class="{ 'mt-1': !porductIndex }"
    >
      {{ t('estimated_handover_date') }}:
      <strong class="text-success">{{ getRelativeDay(carrierHandoverDate) }}</strong>
    </span>
  </div>
</template>

<i18n>
  {
    "cs": {
    "day_after_tomorrow": "Pozítří",
    "estimated_delivery_date": "Předpokládané doručení",
    "estimated_handover_date": "Předpokládané odeslání",
    "now": "Ihned",
    "today": "Dnes",
    "tomorrow": "Zítra"
},
    "sk": {
    "day_after_tomorrow": "Pozajtra",
    "estimated_delivery_date": "Predpokladaný termín dodania",
    "estimated_handover_date": "Predpokladané odoslanie",
    "now": "Okamžite",
    "today": "Dnes",
    "tomorrow": "Zajtra"
},
    "pl": {
    "day_after_tomorrow": "Pojutrze",
    "estimated_delivery_date": "Przewidywany czas dostawy",
    "estimated_handover_date": "Przewidywany czas wysyłki",
    "now": "Natychmiast",
    "today": "Dzisiaj",
    "tomorrow": "Jutro"
},
    "hu": {
    "day_after_tomorrow": "Holnapután",
    "estimated_delivery_date": "Várható szállítás",
    "estimated_handover_date": "Várható kiszallítás",
    "now": "Azonnal",
    "today": "Ma",
    "tomorrow": "Holnap"
},
    "de": {
    "day_after_tomorrow": "Übermorgen",
    "estimated_delivery_date": "Voraussichtliche Lieferung",
    "estimated_handover_date": "Voraussichtlicher Versand",
    "now": "Sofort",
    "today": "Heute",
    "tomorrow": "Morgen"
},
    "at": {
    "day_after_tomorrow": "Übermorgen",
    "estimated_delivery_date": "Voraussichtliche Lieferung",
    "estimated_handover_date": "Voraussichtlicher Versand",
    "now": "Sofort",
    "today": "Heute",
    "tomorrow": "Morgen"
},
    "ro": {
    "day_after_tomorrow": "Peste 2 zile",
    "estimated_delivery_date": "Dată estimată de expediere",
    "estimated_handover_date": "Data estimată de expediere",
    "now": "Imediat",
    "today": "Astăzi",
    "tomorrow": "Mâine"
},
    "fr": {
    "day_after_tomorrow": "Après-demain",
    "estimated_delivery_date": "Livraison prévue",
    "estimated_handover_date": "Expédition prévue",
    "now": "Immédiatement",
    "today": "aujourd'hui",
    "tomorrow": "Demain"
},
    "it": {
    "day_after_tomorrow": "Dopodomani",
    "estimated_delivery_date": "Consegna prevista",
    "estimated_handover_date": "Spedizione prevista",
    "now": "Subito",
    "today": "Oggi",
    "tomorrow": "Domani"
}
  }
</i18n>